import React, {
  useEffect,
  useState,
  useMemo,
  useRef,
} from 'react';
import { t } from 'ttag';
import clsx from 'clsx';
import { useStoreActions, useStoreState } from '@models/RootStore';
import useStyles from './styles';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  CircularProgress,
  FormControlLabel,
  InputAdornment,
  TextField,
  Checkbox,
} from '@material-ui/core';
import LightTooltip from '@components/Tooltip/LightTooltip';
import { orderBy, isEmpty, omit } from 'lodash';
import { Delete } from 'src/svgComponents';
import { Refresh, SvgArrowDownO } from '@icons/index';
import NewTemplateDialog from './NewTemplateDialog';
import ParamMappingDialog from './ParamMappingDialog';
import useBacNet from '@hooks/useBacNet';
import DynamicCircleIcon from '@components/DynamicCircleIcon';
import useTypes from '@hooks/useTypes';
import SvgSearch from '@icons/SearchIcon';
import { filterByTerm } from '@utils/ArrayUtils';
import { getUniqueId } from '@utils/StringUtils';
import useConfirmationDialog from '@components/AddEditBacnetUiTemplateDialog/SaveConfirmationModal';
import {
  Button,
  useConfirmation,
  IconButton,
  FilterRequire,
  Select,
} from '@coolremote/shared';
import EditInPlaceInput from '@components/EditInPlaceInput';
import DetectionIcon from './DetectionIcon';
import BackButton from '../Bacnet/BackButton';
import useDateTime from '@hooks/useDateTime';

/**
 * @typedef {Object} Template
 * @property {string} customer - The ID of the customer associated with the template.
 * @property {string} site - The ID of the site associated with the template.
 * @property {string} syncId - The synchronization ID of the template.
 * @property {string} name - The name of the template.
 * @property {string} model - The model name of the BACnet device.
 * @property {number} vendorId - The vendor ID of the BACnet device.
 * @property {string} createdAt - The ISO 8601 formatted creation timestamp.
 * @property {string} updatedAt - The ISO 8601 formatted last update timestamp.
 * @property {string} id - The unique identifier of the template.
 * @property {Object} data - Additional data associated with the template.
 * @property {Object} permissions - The permissions associated with the template.
 * @property {boolean} permissions.canRead - Indicates if the template can be read.
 * @property {boolean} permissions.canUpdate - Indicates if the template can be updated.
 * @property {boolean} permissions.canDelete - Indicates if the template can be deleted.
 */

const TemplateSelectionTypes = {
  new: {
    type: 'new',
    name: t`New`,
    saveText: t`Create`,
    title: t`Create New Mapping Template`,
    visible: false,
  },
  list: {
    type: 'list',
    name: t`From List`,
    saveText: t`Select`,
    title: t`Select Mapping Template`,
    visible: false,
  },
};

const OBJECT_TYPES = {
  binary: 1,
  'reverse_Binary': 2,
  enumeration: 4,
  value: 5
};


/**
 * Get a human-readable mapping name for a template property.
 *
 * Returns '-' when the mapping object or template info is falsy, or when
 * templateInfo.property is not provided. Otherwise returns the mapped name
 * from `universalEntityPropertiesByEntityType` for the given property key;
 * if no mapping exists, returns the original property key.
 *
 * @param {Object.<string, string>} universalEntityPropertiesByEntityType
 *   Mapping of property keys to display names.
 * @param {Object} templateInfo
 *   Template metadata object that should contain a `property` field.
 * @param {string} [templateInfo.property]
 *   The property key to resolve via the mapping.
 * @returns {string}
 *   Resolved display name, fallback to the property key, or '-' when inputs are invalid.
*/
const getUniversalMappingName = (universalEntityPropertiesByEntityType, templateInfo, type = 'controlUnit') => {

  if (!universalEntityPropertiesByEntityType || !templateInfo) {
    return '-';
  }

  if (!templateInfo.property) {
    return '-';
  }

  if (!universalEntityPropertiesByEntityType[type] || !universalEntityPropertiesByEntityType[type][templateInfo.property]) {
    return templateInfo.property;
  }

  return universalEntityPropertiesByEntityType[type][templateInfo.property].text ?? templateInfo.property;

};

/**
 * BacNetMapping component handles the mapping of BacNet devices to parameter templates.
 * It allows for fetching BacNet device data, displaying a list of objects, and saving new or existing mapping templates.
 *
 * @component
 * @param {Object} props - Component properties.
 *
 * @returns {JSX.Element} The rendered BacNetMapping component.
 */
const BacNetMapping = (props) => {
  const classes = useStyles();
  const { siteId = '', bacnetUnitId = '', handleClose } = props;

  const [showDialog, DialogComponent] = useConfirmationDialog();
  const confirm = useConfirmation();
  const { addMessage } = useStoreActions((action) => action.errorMessage);

  const {
    getSiteBacNetParameterTemplates,
    getBacNetUnitObjects,
    getBacNetUnitById,
    pollBacNetUnitById,
    siteBacNetParameterTemplates,
    isLoading,
    updateBacNetUnit,
    getBacnetIconInformation,
    createMultipleBacNetParameterTemplates,
    updateMultipleBacNetParameterTemplates,
    discoverBacnetDeviceObjects,
    readObjectValues,
  } = useBacNet();

  const {
    bacnetMeasurementUnits,
    bacnetUnitTypes,
    universalEntityPropertiesByEntityType,
    defaultBinaryEnums,
    defaultReverseBinaryEnums
  } = useTypes();

  const saveQueue = useRef({});

  const updateSelections = useStoreActions(
    (a) => a.selections.updateSelections
  );
  const { siteId: selectedSiteId } = useStoreState(
    (s) => s.selections.selections
  );

  const { bacnetMeasurementUnitEnums, bacnetCovOptionsTypes } = useStoreState(
    (state) => state.types
  );

  const getDevice = useStoreState((state) => state.devices.getDevice);
  const [viewAllObject, setViewAllObject] = useState(true);

  const [templateSelectionType, setTemplateSelectionType] = useState(
    TemplateSelectionTypes['new']
  );
  const [bacNetDevice, setbacNetDevice] = useState({});
  const [bacnetUnitObjects, setBacnetUnitObjects] = useState({});
  const [selectedObjectId, setSelectedObjectId] = useState();
  const [isMappingDialogOpen, setIsMappingDialogOpen] = useState();
  const [activeTemplate, setActiveTemplate] = useState(null);
  const [templateName, setTemplateName] = useState('');
  const [deletingObjectId, setDeletingObjectId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const [allTemplates, setAllTemplates] = useState({});

  const [savedTemplateName, setSavedTemplateName] = useState('');
  const [isSavingTemplate, setIsSavingTemplate] = useState(false);
  const [isDiscovering, setIsDiscovering] = useState(false);
  const allDevices = useStoreState((s) => s.devices.allDevices);
  const isRawSupported = useMemo(() => {
    const device = allDevices[bacNetDevice?.device];
    return parseInt(device?.itcVersion, 10) >= 12;
  }, [bacNetDevice, allDevices]);
  const isRawSupportedRef = useRef(isRawSupported);
  isRawSupportedRef.current = isRawSupported;
  const [objectValues, setObjectValues] = useState({});
  const [refreshingObjectIds, setRefreshingObjectIds] = useState({});
  const [isRefreshingAllValues, setIsRefreshingAllValues] = useState(false);

  const pollingRef = useRef(null);

  const { timeStampToDateString } = useDateTime();

  const { objectsDiscoveryStatusTimestamp, objectsDiscoveryStatus, updatedAt } = bacNetDevice || {};
  const recentlyDiscoveredTime = useMemo(() => {

    if (objectsDiscoveryStatus === 1 && objectsDiscoveryStatusTimestamp) {
      return timeStampToDateString(objectsDiscoveryStatusTimestamp);
    }

    if (updatedAt) {
      return timeStampToDateString(updatedAt);
    }

    return null;

  }, [objectsDiscoveryStatusTimestamp, objectsDiscoveryStatus, updatedAt]);

  //If the component is unmounted and isDiscovering polling is active, clear the polling.
  useEffect(() => {
    return () => {
      if (pollingRef.current) {
        clearTimeout(pollingRef.current);
      }
    };
  }, []);

  //This is needed because there is a component local state management for all templates before saving it into the store/BE.
  useEffect(() => {
    setAllTemplates(siteBacNetParameterTemplates);
  }, [siteBacNetParameterTemplates]);

  const templateSelectionOptions = useMemo(
    () => Object.values(TemplateSelectionTypes),
    [TemplateSelectionTypes]
  );

  const maxAllowed = useMemo(() => {
    return getDevice(bacNetDevice?.device)?.maxAllowedBacnetObjectsPerTemplate || 20;
  }, [bacNetDevice]);

  const bacnetUnitTypesLabelsByValueMap = useMemo(() => {
    return Object.values(bacnetUnitTypes).reduce((map, { value, label }) => {
      map[value] = label;
      return map;
    });
  }, [bacnetUnitTypes]);

  const bacnetUnitObjectsOptions = useMemo(() => {
    const unitsArray = Object.values(bacnetUnitObjects || {});

    if (isEmpty(activeTemplate?.data)) {
      return unitsArray;
    }

    return unitsArray.map((unit) => ({
      ...unit,
      templateInfo: activeTemplate.data[unit.objectId] ?? null,
    }));
  }, [bacnetUnitObjects, viewAllObject, activeTemplate]);

  /** @type {Object<string,Object>} a map that holds the mapping objects on the current template by unit `objectId`.  */
  const activeTemplateDataMap = useMemo(() => {
    if (!activeTemplate?.data) {
      return {};
    }
    return activeTemplate.data;
  }, [activeTemplate]);

  const activeTemplateDataMapRef = useRef(activeTemplateDataMap);
  activeTemplateDataMapRef.current = activeTemplateDataMap;

  /** An array of units that has a corresponding mapping */
  const bacnetUnitMappedObjectsOptions = useMemo(
    () => bacnetUnitObjectsOptions.filter(({ templateInfo }) => !!templateInfo),
    [bacnetUnitObjectsOptions]
  );

  useEffect(() => {
    if (!selectedSiteId && siteId) {
      updateSelections({ type: 'site', data: siteId });
    }
    if (selectedSiteId != siteId) {
      handleBack();
    }
  }, [siteId, selectedSiteId]);

  const fetchData = async () => {
    const templates = await getSiteBacNetParameterTemplates(siteId);
    const deviceData = await getBacNetUnitById(bacnetUnitId);
    setbacNetDevice(deviceData);
    const unitObjectsData = await getBacNetUnitObjects(bacnetUnitId);
    setBacnetUnitObjects(unitObjectsData);
    const activeTemplateData = deviceData?.paramTemplate ? templates?.[deviceData.paramTemplate]?.data : null;
    // handleRefreshAllValues(unitObjectsData, deviceData?.bacnetDevice); // CS-1123 1.c
    handleRefreshAllValues(unitObjectsData, deviceData?.bacnetDevice, activeTemplateData);
  };

  const fetchObjects = async () => {
    const unitObjectsData = await getBacNetUnitObjects(bacnetUnitId);
    setBacnetUnitObjects(unitObjectsData);
    // handleRefreshAllValues(unitObjectsData, bacNetDevice?.bacnetDevice); // CS-1123 1.c
    handleRefreshAllValues(unitObjectsData, bacNetDevice?.bacnetDevice, activeTemplateDataMapRef.current);
  };

  const pollUnitInformation = async () => {
    if (document.hidden) {
      return;
    }

    const deviceData = await pollBacNetUnitById(bacnetUnitId);
    if (!deviceData) {
      // Transient network failure (e.g. a backgrounded/throttled tab) - retry on the next tick
      // instead of surfacing an error for something the user didn't do.
      pollingRef.current = setTimeout(() => {
        pollUnitInformation();
      }, 5000);
      return;
    }

    setbacNetDevice(deviceData);
    if (deviceData.objectsDiscoveryStatus === 2 || deviceData.objectsDiscoveryStatus === 3) {
      pollingRef.current = setTimeout(() => {
        pollUnitInformation();
      }, 5000);
    } else {
      setIsDiscovering(false);
      fetchObjects();
    }
  };

  useEffect(() => {
    if (!isDiscovering && (bacNetDevice?.objectsDiscoveryStatus === 2 || bacNetDevice?.objectsDiscoveryStatus === 3)) {
      setIsDiscovering(true);
      pollUnitInformation();
    }
  }, [bacNetDevice]);

  //While the tab is hidden, stop polling instead of hitting a throttled/suspended connection.
  //Resume immediately when it becomes visible again if discovery is still in progress.
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        if (pollingRef.current) {
          clearTimeout(pollingRef.current);
          pollingRef.current = null;
        }
      } else if (isDiscovering) {
        pollUnitInformation();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isDiscovering]);

  useEffect(() => {
    if (!bacnetUnitId || !siteId) {
      return;
    }
    fetchData();
  }, [siteId, bacnetUnitId]);

  useEffect(() => {
    setTemplateName(activeTemplate?.name || '');
  }, [activeTemplate]);

  const generateNewTemplateItem = (name) => {
    const newTemplateDetails = {
      name,
      site: siteId,
      model: bacNetDevice?.modelName,
      vendorId: bacNetDevice?.vendorId,
    };

    return {
      id: getUniqueId(allTemplates),
      data: {},
      type: 'new',
      newTemplateDetails,
      ...newTemplateDetails,
    };
  };

  const { modifiedTemplates, newUnsavedTemplates, editedExistingTemplates } =
    useMemo(() => {
      const modifiedTemplates = Object.values(allTemplates || {}).filter(
        ({ isModified }) => !!isModified
      );
      const newUnsavedTemplates = modifiedTemplates.filter(
        ({ type }) => type === 'new'
      );
      const editedExistingTemplates = modifiedTemplates.filter(
        ({ type }) => type !== 'new'
      );

      return {
        modifiedTemplates,
        newUnsavedTemplates,
        editedExistingTemplates,
      };
    }, [allTemplates]);

  const saveTemplatesToServer = async (
    newTemplatesToSave,
    existingTemplatesToSave = []
  ) => {
    let updatedlist = [];
    const newFullTemplates = await createMultipleBacNetParameterTemplates(
      newTemplatesToSave
    );

    const newItemsToBatchEdit = newFullTemplates.map(
      ({ data, fullTemplate }) => {
        const params = {
          data,
          entityTemplate: fullTemplate.entityTemplate,
        };
        if (fullTemplate.entityTemplate) {
          params.entityType = 'controlUnit';
        }
        return { data: params, id: fullTemplate.id };
      }
    );

    const itemsToBatchEdit = [
      ...newItemsToBatchEdit,
      ...existingTemplatesToSave,
    ];

    if (itemsToBatchEdit.length) {
      setIsSavingTemplate(true);
      updatedlist = await updateMultipleBacNetParameterTemplates(
        itemsToBatchEdit
      );
    }
    setActiveTemplate(updatedlist[0]);
    setIsSavingTemplate(false);
  };

  const handleBack = async () => {
    if (!modifiedTemplates.length) {
      handleClose(activeTemplate?.id, bacNetDevice?.paramTemplate);
      return;
    }
    const ok = await confirm({
      title: t`Save changes?`,
      bodyMessage: getBackDialogText(),
      confirmText: t`Yes`,
      cancelText: t`No`,
    });
    if (ok) {
      saveTemplates();
    } else {
      setAllTemplates({});
      handleClose(activeTemplate?.id, bacNetDevice?.paramTemplate);
    }
  };

  const queueExistingSave = async (exitingTemplates) => {
    if (exitingTemplates) {
      saveQueue.current.items = exitingTemplates;
      saveQueue.current.index = 0;
      saveQueue.current.saveExistingParams = [];
      saveQueue.current.saveNewParams = [];
    }

    const { items, index, saveExistingParams, saveNewParams } =
      saveQueue.current;

    const template = items[index];

    if (!template) {
      if (!saveNewParams.length && !saveExistingParams.length) {
        return; // User canceled all saves, keep local state intact
      }
      return await saveTemplatesToServer(saveNewParams, saveExistingParams);
    }

    setSavedTemplateName(template.name);

    const userResponse = await showDialog();

    //User clicked cancel - skip the current template save.
    if (!userResponse) {
      saveQueue.current.index++;
      return queueExistingSave();
    }

    const { isNew, newName } = userResponse;

    if (isNew && newName) {
      const newItem = generateNewTemplateItem(newName);
      saveQueue.current.saveNewParams.push({
        ...newItem,
        data: template.data,
      });
    } else {
      const { data, id } = template;
      const params = { data, entityTemplate: template.entityTemplate };
      if (template.entityTemplate) {
        params.entityType = 'controlUnit';
      }
      saveQueue.current.saveExistingParams.push({ data: params, id });
    }

    saveQueue.current.index++;
    queueExistingSave();
  };

  const saveTemplates = async () => {
    if (!modifiedTemplates.length) {
      return handleBack();
    }

    //save the current information for the function.
    const newTemplates = [...newUnsavedTemplates];
    const exitingTemplates = [...editedExistingTemplates];

    if (newTemplates.length) {
      saveTemplatesToServer(newTemplates);
    }

    if (exitingTemplates.length) {
      queueExistingSave(exitingTemplates);
    }
  };

  /**
   * Handles the toggle of the menu.
   * @param {Object} event - The event object.
   * @returns {void}
   */


  /**
   * Saves a new or existing mapping template.
   * @param {string} selectedTemplateId - The ID of the selected template.
   */
  const handleSaveTemplate = async (selectedTemplateId) => {

    if (selectedTemplateId) {
      const selectedTemplateToSave = allTemplates[selectedTemplateId] ?? siteBacNetParameterTemplates[selectedTemplateId]
      if (!selectedTemplateToSave) {
        console.error(`Template with id ${selectedTemplateId} not found`);
        return;
      }
      setActiveTemplate({
        ...selectedTemplateToSave,
        type: 'list',
      });
      setTemplateSelectionType(TemplateSelectionTypes['list']);
    } else {
      const newTemplateDetails = {
        name: templateName,
        site: siteId,
        model: bacNetDevice.modelName,
        vendorId: bacNetDevice.vendorId,
      };

      const newTemplate = {
        id: getUniqueId(allTemplates),
        data: {},
        type: 'new',
        newTemplateDetails,
        ...newTemplateDetails,
      };

      setAllTemplates((oldAllTemplates) => ({
        ...oldAllTemplates,
        [newTemplate.id]: newTemplate,
      }));
      setActiveTemplate(newTemplate);
      setTemplateSelectionType(TemplateSelectionTypes['new']);
    }
  };

  useEffect(() => {
    if (bacNetDevice?.paramTemplate) {
      handleSaveTemplate(bacNetDevice.paramTemplate);
    }
  }, [bacNetDevice]);

  const handleDelete = async (event, objectId) => {
    event.stopPropagation();
    if (deletingObjectId || !activeTemplateDataMap[objectId]) {
      return;
    }
    setDeletingObjectId(objectId);
    try {
      const updatedData = omit(activeTemplate?.data, objectId);
      const updatedTemplate = {
        ...activeTemplate,
        data: updatedData,
        isModified: true,
      };
      setActiveTemplate(updatedTemplate);
      setAllTemplates((oldAllTemplates) => ({
        ...oldAllTemplates,
        [updatedTemplate.id]: updatedTemplate,
      }));
    } finally {
      setDeletingObjectId(null);
    }
  };

  const handleSaveTemplateUpdates = (id, newData) => {
    const updatedTemplate = allTemplates[id];
    const { updateUniversalMapping } = newData;

    if (updatedTemplate) {
      if (!updateUniversalMapping) {
        updatedTemplate.data = newData;
      } else {
        updatedTemplate.entityTemplate = newData.entityTemplate;
        if (newData.entityTemplate) {
          updatedTemplate.entityType = 'controlUnit';
        }
      }
      updatedTemplate.isModified = true;
      setActiveTemplate({ ...updatedTemplate });
      setAllTemplates((oldAllTemplates) => ({
        ...oldAllTemplates,
        [id]: { ...updatedTemplate },
      }));
      handleMappingDialogClose();
    } else {
      console.error(`Can't find template ${id}`);
    }
  };

  const closeTemplateSelectionDialog = () => {
    setTemplateSelectionType((prevParam) => ({
      ...prevParam,
      visible: false,
    }));
  };

  const handleRefresh = async () => {
    if (!bacNetDevice?.isConnected) {
      addMessage({
        message: t`The Edge/BACnet device is currently disconnected. Please try again after resolving the issue.`,
        hideReportButton: true,
      });
      return;
    }
    try {
      setIsDiscovering(true);
      await discoverBacnetDeviceObjects(bacNetDevice?.bacnetDevice);
      pollUnitInformation();
    } catch (e) {
      addMessage({ message: e.message });
      setIsDiscovering(false);
    }
  };

  const handleRefreshAllValues = async (objectsToRefresh, deviceId, templateData) => {
    const targetDeviceId = deviceId;
    if (!targetDeviceId || !isRawSupportedRef.current) {
      return;
    }

    let sourceObjects = objectsToRefresh
      ? Object.values(objectsToRefresh)
      : tableRows;

    /*
    // CS-1123 1.c: Automatically retrieve initial value to each of the parameters in the list
    const objectIds = sourceObjects
      .map((row) => row.objectId)
      .filter((id) => !!id);
    */

    const mappingData = templateData !== undefined ? templateData : activeTemplateDataMapRef.current;
    if (isEmpty(mappingData)) {
      return;
    }
    sourceObjects = sourceObjects.filter(({ objectId }) => !!mappingData[objectId]);

    const objectIds = sourceObjects
      .map((row) => row.objectId)
      .filter((id) => !!id);

    if (objectIds.length === 0) {
      return;
    }

    setIsRefreshingAllValues(true);
    setRefreshingObjectIds((prev) => {
      const next = { ...prev };
      objectIds.forEach((id) => { next[id] = true; });
      return next;
    });
    try {
      const result = await readObjectValues(targetDeviceId, objectIds);
      const values = result?.objects || result?.data?.objects || {};
      const newObjectValues = {};
      Object.keys(values).forEach((id) => {
        if (values[id].rc === 0) {
          newObjectValues[id] = values[id].value;
        }
      });
      setObjectValues((prev) => ({ ...prev, ...newObjectValues }));
      if (!result) {
        addMessage({ message: t`Failed to fetch object values` });
      }
    } catch (e) {
      addMessage({ message: e.message || t`Failed to fetch object values` });
    } finally {
      setIsRefreshingAllValues(false);
      setRefreshingObjectIds((prev) => {
        const next = { ...prev };
        objectIds.forEach((id) => { delete next[id]; });
        return next;
      });
    }
  };

  const getCustomEnumValue = (row, unitParamTemplate, value) => {
    if (value === undefined || value === null) {
      return value;
    }

    // Find the parameter definition by objectId in the template or use the already attached templateInfo
    const paramDef = row.templateInfo || unitParamTemplate?.data?.[row.objectId];

    let normalizedValue = value;
    if (typeof value === 'number') {
      const step = parseFloat(paramDef?.step);
      if (!isNaN(step) && step > 0) {
        // Round to the nearest multiple of step, and fix floating point precision
        normalizedValue = parseFloat((Math.round(value / step) * step).toFixed(10));
      } else if (
        row.objectType === OBJECT_TYPES.binary ||
        row.objectType === OBJECT_TYPES.reverse_Binary ||
        row.objectType === OBJECT_TYPES.enumeration
      ) {
        normalizedValue = Math.round(value);
      } else {
        // Round to avoid floating point precision issues like 22.79999
        normalizedValue = Math.round(value * 1000) / 1000;
      }
    }

    if (paramDef?.customEnum) {
      const label = paramDef.customEnum[String(normalizedValue)];
      if (label !== undefined) {
        return label;
      }
    }

    if (
      (row.objectType === OBJECT_TYPES.binary || row.objectType === OBJECT_TYPES.reverse_Binary) &&
      (normalizedValue === 0 || normalizedValue === 1)
    ) {
      const isReverse = row.objectType === OBJECT_TYPES.reverse_Binary;
      const enums = isReverse ? defaultReverseBinaryEnums : defaultBinaryEnums;

      const label = row.enumeration?.[normalizedValue]
        || row.bacnetProps?.[(normalizedValue === 1 && !isReverse) || (normalizedValue === 0 && isReverse) ? 'ACTIVE_TEXT' : 'INACTIVE_TEXT']
        || Object.values(enums || {}).find((e) => e.value === normalizedValue)?.label;

      if (label) {
        return label;
      }
    }

    // Fallback to built-in BACnet enumeration if available on the object
    if (
      row.enumeration &&
      normalizedValue !== undefined &&
      normalizedValue !== null
    ) {
      return row.enumeration[normalizedValue];
    }

    return normalizedValue;
  };

  const handleRowClick = (object) => {
    if (isEmpty(activeTemplate)) {
      confirm({ bodyMessage: t`Please select a template or create a new one before mapping the objects.`, confirmText: t`Ok` });
      return;
    }

    const hasReachedMax = bacnetUnitMappedObjectsOptions?.length >= maxAllowed;
    const isObjectAlreadyMapped = bacnetUnitMappedObjectsOptions?.some(item => item?.id === object);

    if (hasReachedMax && !isObjectAlreadyMapped) {
      confirm({ bodyMessage: t`The maximum number of mapped objects allowed is ${maxAllowed}`, confirmText: t`Ok` });
      return;
    }
    setSelectedObjectId(object);
    setIsMappingDialogOpen(true);
  };

  const handleMappingDialogClose = () => {
    setIsMappingDialogOpen(false);
  };

  const handleAppNameChange = async (newValue) => {
    const { id } = bacNetDevice;
    await updateBacNetUnit({
      id,
      data: {
        title: newValue,
      },
    }).then(() => {
      setbacNetDevice((prev) => ({ ...prev, title: newValue }));
    });
  };

  const handleRefreshObjectValue = async (event, objectId) => {
    event.stopPropagation();
    if (refreshingObjectIds[objectId]) {
      return;
    }
    setRefreshingObjectIds((prev) => ({ ...prev, [objectId]: true }));
    try {
      const result = await readObjectValues(bacNetDevice?.bacnetDevice, [objectId]);
      if (!result || (!result.success && result.message)) {
        addMessage({ message: result?.message || t`Failed to fetch object value` });
        return;
      }
      const objectData = result?.objects?.[objectId];
      if (objectData?.rc === 0) {
        setObjectValues((prev) => ({ ...prev, [objectId]: objectData.value }));
      }
    } catch (e) {
      addMessage({ message: e.message || t`Failed to fetch object value` });
    } finally {
      setRefreshingObjectIds((prev) => {
        const next = { ...prev };
        delete next[objectId];
        return next;
      });
    }
  };

  const handleSearchInputChange = ({ target }) => {
    const { value } = target;

    setSearchTerm(value);
  };

  const tableRows = useMemo(() => {
    const activeOptions = viewAllObject
      ? bacnetUnitObjectsOptions
      : bacnetUnitMappedObjectsOptions;
    return orderBy(activeOptions, ['name'], ['asc']);
  }, [viewAllObject, bacnetUnitObjectsOptions, bacnetUnitMappedObjectsOptions]);

  const filteredTableRows = useMemo(() => {
    if (!tableRows) {
      return [];
    }
    if (!searchTerm) {
      return tableRows;
    }
    return filterByTerm(tableRows, searchTerm, ['objectId', 'name']);
  }, [tableRows, searchTerm]);

  const getBackDialogText = () => {
    const hasEdited = !!editedExistingTemplates.length;
    const hasNew = !!newUnsavedTemplates.length;

    if (hasEdited && hasNew) {
      return t`You have created ${newUnsavedTemplates.length} new template and changed ${editedExistingTemplates.length} existing templates. would you like to save your changes?`;
    }

    if (hasEdited && !hasNew) {
      return t`You have changed ${editedExistingTemplates.length} existing templates. would you like to save your changes?`;
    }

    if (!hasEdited && hasNew) {
      return t`You have created ${newUnsavedTemplates.length} new template would you like to save your changes?`;
    }

    return '';
  };

  const getParsedRowData = (row) => {
    const dynamicWidth = 'calc((43% - 62px) / 3)';
    const { id, objectId, name, bacnetProps, bacnetType, templateInfo } = row;
    const {
      color,
      shortName,
      name: fullName,
    } = getBacnetIconInformation(bacnetType);
    const { UNITS } = bacnetProps;
    const units = bacnetMeasurementUnits[UNITS] ?? '-';
    const {
      name: mappedName = '-',
      type: mappedType,
      units: mappedUnits,
      cov: mappedCov = '-',
      readOnly: mappedRO = false,
      customEnum,
    } = templateInfo ?? {};

    const displayMappedType =
      bacnetUnitTypesLabelsByValueMap[mappedType] ?? '-';
    const displayMappedUnits = bacnetMeasurementUnitEnums?.[mappedUnits] ?? '-';
    const displayMappedCov =
      bacnetCovOptionsTypes?.[mappedCov]?.toUpperCase() ?? '-';
    const displayMappedRO = mappedRO ? (t`Yes`).toUpperCase() : '-';



    const displayMappedUniversal = getUniversalMappingName(universalEntityPropertiesByEntityType, templateInfo);

    return {
      dynamicWidth,
      id,
      objectId,
      name,
      color,
      shortName,
      fullName,
      units,
      mappedName,
      displayMappedType,
      displayMappedUnits,
      displayMappedCov,
      displayMappedRO,
      displayMappedUniversal,
      isMapped: !!templateInfo,
    };
  };

  const InformationTable = () => {
    return (
      <TableContainer className={classes.tableContainer}>
        <Table stickyHeader aria-label="a dense table">
          <TableHead>
            <TableRow>
              <TableCell classes={{ root: classes.tableSmallHeadCell }} />
              <TableCell classes={{ root: classes.tableHeadCell }} align="left">
                {t`OBJECT ID`}
              </TableCell>
              <TableCell classes={{ root: classes.tableHeadCell }} align="left">
                {t`NAME`}
              </TableCell>
              <TableCell classes={{ root: classes.tableHeadCell }} align="left">
                {t`UNITS`}
              </TableCell>
              <TableCell classes={{ root: classes.valueHeadCell }} align="left">
                {t`Current Value`}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!isLoading.getBacNetUnitObjects &&
              filteredTableRows.map((row) => {
                const {
                  dynamicWidth,
                  id,
                  objectId,
                  name,
                  color,
                  shortName,
                  fullName,
                  units,
                } = getParsedRowData(row);

                const isRefreshing = !!refreshingObjectIds[objectId];

                return (
                  <TableRow
                    key={id}
                    hover
                    className={clsx(selectedObjectId == id && classes.selectedObject)}
                    onDoubleClick={() => handleRowClick(id)}
                  >
                    <TableCell
                      align="center"
                      classes={{ root: classes.tableCell }}
                      style={{ width: 62 }}
                    >
                      <DynamicCircleIcon
                        color={color}
                        content={shortName}
                        title={fullName}
                        width={32}
                        height={32}
                      />
                    </TableCell>
                    <TableCell
                      classes={{
                        root: clsx(classes.tableCell, classes.tableCellBold, classes.nowrap),
                      }}
                      align="left"
                      style={{ width: dynamicWidth }}
                    >
                      {objectId}
                    </TableCell>
                    <TableCell
                      classes={{ root: classes.tableCell }}
                      align="left"
                      style={{ width: dynamicWidth }}
                    >
                      {name}
                    </TableCell>
                    <TableCell
                      classes={{ root: classes.tableCell }}
                      align="left"
                      style={{ width: dynamicWidth }}
                    >
                      {units}
                    </TableCell>
                    <TableCell
                      classes={{ root: classes.tableCell }}
                      align="left"
                    >
                      <div className={classes.valueCellContent}>
                        {!isRawSupported ? (
                          <LightTooltip classes={{ tooltip: classes.versionTooltip }} title={t`Value retrieval requires EDGE version 1.9.0 or higher`}>
                            <span className={classes.NAValue}>{t`N/A`}</span>
                          </LightTooltip>
                        ) : (
                          <>
                            <span>
                              {getCustomEnumValue(
                                row,
                                activeTemplate ?? allTemplates[bacNetDevice?.paramTemplate],
                                objectValues[objectId]
                              ) ?? '-'}
                            </span>
                            <LightTooltip classes={{ tooltip: classes.versionTooltip }} title={t`Click to retrieve value`}>
                              <span>
                                <IconButton
                                  size="small"
                                  className={clsx(
                                    classes.valueRefreshBtn,
                                    isRefreshing && classes.spinning
                                  )}
                                  onClick={(event) =>
                                    handleRefreshObjectValue(event, objectId)
                                  }
                                  disabled={isRefreshing}
                                >
                                  <Refresh width={16} height={16} />
                                </IconButton>
                              </span>
                            </LightTooltip>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const MappingTable = () => {
    return (
      <TableContainer className={classes.tableContainer}>
        <Table stickyHeader aria-label="a dense table">
          <TableHead>
            <TableRow>
              <TableCell classes={{ root: classes.tableHeadCell }} align="left">
                {t`NAME`}
              </TableCell>
              <TableCell classes={{ root: classes.tableHeadCell }} align="left">
                {t`TYPE`}
              </TableCell>
              <TableCell classes={{ root: classes.tableHeadCell }} align="left">
                {t`UNITS`}
              </TableCell>
              <TableCell classes={{ root: classes.tableHeadCell }} align="left">
                {t`R/O`}
              </TableCell>
              <TableCell classes={{ root: classes.tableHeadCell }} align="left">
                {t`Universal`}
              </TableCell>
              <TableCell classes={{ root: classes.tableHeadCell }} align="left">
                {t`COV`}
              </TableCell>
              <TableCell
                classes={{ root: classes.tableSmallHeadCell }}
                align="center"
              />
            </TableRow>
          </TableHead>
          <TableBody>
            {!isLoading.getBacNetUnitObjects &&
              filteredTableRows.map((row) => {
                const {
                  id,
                  objectId,
                  mappedName,
                  displayMappedType,
                  displayMappedUnits,
                  displayMappedCov,
                  displayMappedRO,
                  displayMappedUniversal,
                  isMapped,
                } = getParsedRowData(row);
                return (
                  <TableRow
                    key={id}
                    hover
                    className={clsx(selectedObjectId == id && classes.selectedObject)}
                    onDoubleClick={() => handleRowClick(id)}
                  >
                    <TableCell
                      classes={{
                        root: clsx(classes.tableCell, classes.tableCellBold, classes.nowrap),
                      }}
                      align="left"
                    >
                      {mappedName}
                    </TableCell>
                    <TableCell
                      classes={{ root: classes.tableCell }}
                      align="left"
                    >
                      {displayMappedType}
                    </TableCell>
                    <TableCell
                      classes={{ root: classes.tableCell }}
                      align="left"
                    >
                      {displayMappedUnits}
                    </TableCell>
                    <TableCell
                      classes={{ root: classes.tableCell }}
                      align="left"
                    >
                      {displayMappedRO}
                    </TableCell>
                    <TableCell
                      classes={{ root: classes.tableCell }}
                      align="left"
                    >
                      {displayMappedUniversal}
                    </TableCell>
                    <TableCell
                      classes={{ root: classes.tableCell }}
                      align="left"
                    >
                      {displayMappedCov}
                    </TableCell>
                    <TableCell
                      align="center"
                      classes={{ root: classes.tableCell }}
                    >
                      {isMapped && <IconButton
                        onClick={(event) => handleDelete(event, objectId)}
                        disabled={deletingObjectId === objectId}
                        className={classes.mappbingTableDeleteBtn}
                      >
                        <Delete />
                      </IconButton>}
                    </TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const renderContent = () => {
    if (!bacnetUnitId) {
      return <FilterRequire type={t`device`} />;
    }

    if (isLoading.getSiteBacNetParameterTemplates) {
      return (
        <div className={classes.loaderContainer}>
          <CircularProgress style={{ alignSelf: 'center' }} />
        </div>
      );
    }

    /**
     * Handles the change event for the Map to universal AC unit checkbox.
     * @param {*} event
     */
    const handleMTUACUChange = (event) => {
      handleSaveTemplateUpdates(activeTemplate.id, {
        entityTemplate: event.target.checked,
        updateUniversalMapping: true,
      });
    };

    return (
      <div className={classes.pageView}>
        <div className={classes.pageHeader}>
          <div className={classes.titleContainer}>
            <BackButton onClick={handleBack} />
            <div className={classes.pageTitle}>
              <div>{t`Object Mapping`}</div>

            </div>
            <div className={classes.ids}>
              <span>{bacNetDevice?.name}</span>
              <span>{bacNetDevice?.bacnetId}</span>
            </div>
          </div>
          <div className={classes.headerActionBlock}>
            <div>
              <TextField
                placeholder="Search"
                fullWidth
                value={searchTerm}
                onChange={handleSearchInputChange}
                variant="outlined"
                className={clsx(
                  classes.headerActionBlock__input,
                  classes.searchField
                )}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="start">
                      <SvgSearch />
                    </InputAdornment>
                  ),
                }}
              />
            </div>
          </div>
          <div className={classes.headerActionBlock}>
            <div className={classes.headerActionBlock__label}>
              {t`App Device Name`}
            </div>
            <div>
              <EditInPlaceInput
                value={bacNetDevice?.title ?? ''}
                onSave={handleAppNameChange}
              />
            </div>
          </div>
          <div className={classes.headerActionBlock}>
            <div className={classes.headerActionBlock__label}>
              {t`Mapping Template`}
            </div>
            <div>
              <Select
                value=""
                onChange={(val) => {
                  setTemplateSelectionType({
                    ...TemplateSelectionTypes[`${val}`],
                    visible: true,
                  });
                }}
                options={templateSelectionOptions.map(({ type, name }) => ({
                  value: type,
                  label: name,
                }))}
                placeholder={
                  activeTemplate
                    ? activeTemplate.name
                    : templateSelectionType.name
                }
                className={classes.headerActionBlock__templateSrcMenu}
                classes={{ trigger: classes.headerActionBlock__templateTrigger }}
                menuPortal={false}
              />
            </div>
          </div>
          <div className={classes.headerActionBlock}>
            <div>
              <Button
                variant="contained"
                color="primary"
                disabled={!modifiedTemplates.length || isSavingTemplate}
                onClick={saveTemplates}
              >
                {isSavingTemplate ? <CircularProgress size={20} /> : t`Save`}
              </Button>
            </div>
          </div>
        </div>
        <div className={classes.mainContainer}>
          <Grid container spacing={2}>
            <Grid item xs={6} className={classes.bacnetInfoContainer}>
              <div className={clsx(classes.block)}>
                <div className={classes.block__header}>
                  <div
                    className={classes.block__title}
                  >
                    {t`BACnet Information`}
                    {recentlyDiscoveredTime ? <div className={classes.block_discovery_time}>{t`Last object discovery: `}{recentlyDiscoveredTime}</div> : null}
                  </div>
                  <div className={classes.block__actions}>
                    <div className={classes.block__actions__filters}>
                      <div className={classes.allMappedFilter}>
                        <div
                          className={clsx(viewAllObject && 'IsSelected')}
                          onClick={() => setViewAllObject(true)}
                        >
                          {t`All Objects`}{' '}
                          {`(${bacnetUnitObjectsOptions?.length})`}
                        </div>
                        <div
                          className={clsx(!viewAllObject && 'IsSelected')}
                          onClick={() => setViewAllObject(false)}
                        >
                          {t`Mapped`}{' '}
                          {`(${bacnetUnitMappedObjectsOptions?.length})`}
                        </div>
                      </div>
                      <IconButton
                        onClick={handleRefresh}
                        className={classes.refreshBtn}
                        disabled={isDiscovering}
                      >
                        <Refresh width={20} height={20} />
                      </IconButton>
                    </div>
                    {isDiscovering ? (
                      <div className={classes.block__actions__indicator}>
                        {t`Discovery in progress`}
                        <DetectionIcon />
                      </div>
                    ) : null}
                  </div>
                </div>
                <div className={classes.block__body}>
                  <InformationTable />
                </div>
              </div>
            </Grid>
            <Grid item xs={6}>
              <div className={clsx(classes.block)}>
                <div className={classes.block__header}>
                  <div
                    className={classes.block__title}
                  >{t`Mapping Information`}</div>
                  <div className={classes.block__universal}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          onChange={handleMTUACUChange}
                          checked={activeTemplate?.entityTemplate || false}
                          disabled={isEmpty(activeTemplate)}
                        />
                      }
                      label={t`Map to universal AC unit`}
                    />
                  </div>
                </div>
                <div className={classes.block__body}>
                  <MappingTable />
                </div>
              </div>
            </Grid>
          </Grid>
          {templateSelectionType.visible && (
            <NewTemplateDialog
              templateName={templateName}
              setTemplateName={setTemplateName}
              templateSelectionType={templateSelectionType}
              templatesList={allTemplates}
              handleClose={closeTemplateSelectionDialog}
              handleSave={handleSaveTemplate}
            />
          )}
          {isMappingDialogOpen && !isEmpty(activeTemplate) && (
            <ParamMappingDialog
              bacNetObject={bacnetUnitObjects[selectedObjectId]}
              template={activeTemplate}
              handleSave={handleSaveTemplateUpdates}
              handleClose={handleMappingDialogClose}
              bacnetUnitId={bacnetUnitId}
              currentValue={objectValues[bacnetUnitObjects[selectedObjectId]?.objectId]}
              isRefreshingValue={!!refreshingObjectIds[bacnetUnitObjects[selectedObjectId]?.objectId] || isRefreshingAllValues}
              onRefreshValue={(event) => handleRefreshObjectValue(event, bacnetUnitObjects[selectedObjectId]?.objectId)}
              isRawSupported={isRawSupported}
            />
          )}
        </div>
      </div>
    );
  };


  return (
    <div className={classes.view}>
      <div className={classes.contentArea}>
        {renderContent()}
      </div>
      <DialogComponent templateName={savedTemplateName} />
    </div>
  );
};

export default BacNetMapping;
